import { inject, injectable } from 'inversify'
import { pick } from 'lodash-es'
import { nanoid } from 'nanoid/non-secure'
import { Group, Vector2 } from 'three'
import type { Line2 } from 'three/examples/jsm/lines/Line2.js'
import { setup } from 'xstate'
import { TYPES } from '@/plugins/cad/module/container/types'
import type { KeyEventParams, MouseEventParams } from '@/plugins/cad/module/core/common/event'
import { StateMachineTool } from '@/plugins/cad/module/core/tool/StateMachineTool'
import { CadTipKey } from '@/plugins/cad/module/i18n/tipKeys'
import { DrawUtils } from '@/plugins/cad/module/utils/DrawUtils'
import { KeyCodes } from '@/shared/constants'
import type { AuxiliaryMouseHelper } from '../auxiliaryTool/auxiliaryMouseHelper'
import { type MouseTipHelper, MouseTipId } from '../auxiliaryTool/MouseTipHelper'
import type { CADController } from '../CADController'
import { DrawEntityCommand } from '../command/DrawEntityCommand'
import type { CADViewManager } from '../manager/CADViewManager'
import type { HistoryManager } from '../manager/HistoryManager'
import type { IPoint } from '../model/common/interface'
import { RayEntity } from '../model/entity'
import { createCadLine2Object } from '../object/material/CadLineFactory'
import { MaterialUtils } from '../object/material/MaterialUtils'
import type { EntitySelector } from '../selector/entitySelector'

interface IEntityParams {
  startPoint: IPoint
  unitVector: IPoint
}

const rayMachine = setup({
  actions: {
    showMouseTip: (..._args: any) => {},
    entryFinished: () => {},
  },
}).createMachine({
  id: 'drawRayTool',
  initial: 'idle',
  states: {
    idle: {
      on: {
        NEXT: [{ target: 'draw.point1' }],
      },
    },
    draw: {
      initial: 'point1',
      states: {
        point1: {
          on: { NEXT: 'point2' },
          entry: { type: 'showMouseTip', params: CadTipKey.draw.ray.startPoint },
        },
        point2: {
          on: { NEXT: '#drawRayTool.finished' },
          entry: { type: 'showMouseTip', params: CadTipKey.draw.ray.pointOnRay },
        },
      },
    },
    finished: { entry: 'entryFinished', type: 'final' },
  },
})

/**
 * 射线绘制工具，依次拾取起点与方向点生成无限射线。
 */
@injectable()
export class DrawRayTool extends StateMachineTool<typeof rayMachine> {
  /** 工具名，等同于容器注册的符号 */
  name = TYPES.DrawRayTool

  @inject(TYPES.CADViewManager)
  private readonly viewManager!: CADViewManager

  @inject(TYPES.EntitySelector)
  private readonly entitySelector!: EntitySelector

  @inject(TYPES.MouseTipHelper)
  private readonly mouseTipHelper!: MouseTipHelper

  @inject(TYPES.HistoryManager)
  private readonly historyManager!: HistoryManager

  @inject(TYPES.AuxiliaryMouseHelper)
  private readonly auxiliaryMouseHelper!: AuxiliaryMouseHelper

  protected machine = rayMachine.provide({
    actions: {
      showMouseTip: (_v, tip) => {
        this.showMouseTip(tip)
      },
      entryFinished: () => {
        this.updateEntity()
        this.exit()
      },
    },
  })

  /** 所属控制器，由框架自动注入 */
  declare controller: CADController
  private points: IPoint[] = []
  private tempPoint: IPoint | null = null
  private group = new Group()
  private entityObject: Line2 | null = null
  private entity: RayEntity | null = null
  private currentCommand: DrawEntityCommand | null = null
  private entityParams: IEntityParams | null = null

  protected onInitialize(entityData?: Partial<RayEntity>): void {
    this.viewManager.attachTempObject(this.group)
    this.entitySelector.clearSelection()
    this.inject(this.auxiliaryMouseHelper)

    this.entity = this.createEntity(entityData)
    this.currentCommand = new DrawEntityCommand(
      this.entity,
      this.viewManager,
      this.handleUndo.bind(this),
      this.isCanCommit.bind(this),
      this.isCanUndoStep.bind(this)
    )
    this.historyManager.startCommand(this.currentCommand)
    this.send({ type: 'NEXT' })
  }

  protected onTerminate(): void {
    this.historyManager.commitCommand()
    this.entityObject && DrawUtils.disposeObject(this.entityObject)
    this.group.parent?.remove(this.group)
    this.mouseTipHelper.removeTip(MouseTipId.Draw)
    this.eject(this.auxiliaryMouseHelper)

    this.points = []
    this.tempPoint = null
    this.entityObject = null
    this.entity = null
    this.currentCommand = null
    this.entityParams = null
  }

  /**
   * 鼠标按下：左键依次确认射线起点与方向点。
   *
   * @param params - 鼠标事件参数
   */
  /************ 鼠标键盘事件处理 ************/
  onMouseDown(params: MouseEventParams) {
    if (params.event.button === 0) {
      // 左键
      if (this.tempPoint) {
        this.points.push({ x: this.tempPoint.x, y: this.tempPoint.y })
        this.tempPoint = null
        this.send({ type: 'NEXT' })
        this.updateCanvas()
        this.historyManager.refresh()
      }
    } else if (params.event.button === 2) {
      // 右键
    }
  }

  /**
   * 鼠标移动：更新射线方向预览点。
   *
   * @param params - 鼠标事件参数
   */
  onMouseMove(params: MouseEventParams) {
    this.tempPoint = { x: params.worldPos.x, y: params.worldPos.y }
    this.mouseCapture(params)
    this.updateCanvas()
  }

  /**
   * 键盘按下：ESC 退出工具。
   *
   * @param params - 键盘事件参数
   */
  onKeyDown(params: KeyEventParams) {
    if (params.event.keyCode === KeyCodes.ESC) {
      this.exit()
    }
  }

  // 正交捕捉处理
  private mouseCapture(params: MouseEventParams) {
    if (!this.tempPoint) {
      return
    }
    const startPoint = this.points[this.points.length - 1]
    const finalPoint = this.auxiliaryMouseHelper.mouseCapture(params, { startPoint })
    this.tempPoint = { x: finalPoint.x, y: finalPoint.y }
  }

  /************ 状态机状态处理 ************/
  private showMouseTip(tip: string) {
    this.mouseTipHelper.addTip(MouseTipId.Draw, tip)
  }

  /************ 渲染 ************/
  private updateCanvas() {
    this.updateObject()
  }

  private updateObject() {
    const points = [...this.points]
    if (this.tempPoint) {
      points.push(this.tempPoint)
    }
    if (points.length < 2) {
      return
    }
    const entityParams = this.getEntityParams(points[0], points[1])
    this.entityParams = entityParams
    this.updateEntityObject(entityParams)
  }

  private updateEntityObject(params: IEntityParams | null) {
    if (!params || !this.entity) {
      this.entityObject && (this.entityObject.visible = false)
      return
    }
    const points = [
      params.startPoint,
      new Vector2(params.startPoint.x, params.startPoint.y).add(
        new Vector2(params.unitVector.x, params.unitVector.y).multiplyScalar(1000)
      ),
    ]
    if (!this.entityObject) {
      const material = MaterialUtils.createMaterialByLineType(
        this.entity.lineType,
        MaterialUtils.getMaterialOptionByEntity(this.entity)
      )
      const geometry = DrawUtils.getLineGeometryByPoints(points)
      this.entityObject = createCadLine2Object(geometry, material)
      this.entityObject.computeLineDistances()
      this.group.add(this.entityObject)
      return
    }
    this.entityObject.geometry.setPositions(points.flatMap((p) => [p.x, p.y, 0]))
    this.entityObject.geometry.attributes.position.needsUpdate = true
    this.entityObject.computeLineDistances()
    this.entityObject.visible = true
  }

  /************ 数据创建 ************/
  private createEntity(entityData?: Partial<RayEntity>) {
    const entity = new RayEntity({
      ...this.viewManager.getEntityDefaultData(entityData),
      id: nanoid(),
    })
    return entity
  }

  private updateEntity() {
    if (this.entity && this.entityParams) {
      Object.assign(this.entity, pick(this.entityParams, ['startPoint', 'unitVector']))
    }
  }

  private handleUndo() {
    if (this.isCanUndoStep()) {
      this.points.pop()
      this.updateCanvas()
      this.send({ type: 'PREVIOUS' })
    }
  }

  private isCanCommit(): boolean {
    return this.entity ? !!(this.entity.startPoint && this.entity.unitVector) : false
  }

  private isCanUndoStep(): boolean {
    return this.points.length > 1
  }

  private getEntityParams(p1: IPoint, p2: IPoint): IEntityParams | null {
    const unitVector = new Vector2(p2.x - p1.x, p2.y - p1.y)
    if (unitVector.lengthSq() < 1e-12) {
      return null
    }

    unitVector.normalize()
    return {
      startPoint: p1,
      unitVector: {
        x: unitVector.x,
        y: unitVector.y,
      },
    }
  }
}
